/**
 * CO-VARIANT-SYNC.JS
 * 
 * Handles variant picker and buy button synchronization for custom sections
 * where the default Horizon theme HTML fetching doesn't work (e.g., homepage sections).
 * 
 * This script intercepts variant changes and directly updates:
 * - The hidden variant ID input in the product form
 * - The add to cart button state (enabled/disabled)
 * - The add to cart button text (Add to cart / Sold out)
 * - Preserves variant prices after Horizon's HTML morphing
 * 
 * Usage:
 * 1. Add this script to your theme's assets folder
 * 2. Include it in your section: {{ 'co-variant-sync.js' | asset_url | script_tag }}
 * 3. Add data-co-variant-sync attribute to your section wrapper
 * 
 * Example:
 * <div data-co-variant-sync>
 *   {% render 'co-element-variant-picker', ... %}
 *   {% render 'co-element-buy-buttons', ... %}
 * </div>
 */

(function() {
  'use strict';

  // Configuration
  const SELECTORS = {
    syncContainer: '[data-co-variant-sync]',
    variantPicker: 'variant-picker',
    productForm: 'product-form-component',
    variantInput: 'input[name="id"]',
    addToCartButton: '[ref="addToCartButton"]',
    addToCartContainer: 'add-to-cart-component',
    acceleratedCheckout: '[ref="acceleratedCheckoutButtonContainer"]',
    quantitySelector: 'quantity-selector-component',
    priceElement: '.variant-option__button-label__price'
  };

  // Translations (fallbacks if Theme.translations is not available)
  const DEFAULT_TRANSLATIONS = {
    addToCart: 'Add to cart',
    soldOut: 'Sold out',
    unavailable: 'Unavailable'
  };

  // Store price data globally per container to survive morphs
  const priceDataStore = new WeakMap();

  /**
   * Get translation string
   */
  function getTranslation(key) {
    if (typeof Theme !== 'undefined' && Theme.translations) {
      return Theme.translations[key] || DEFAULT_TRANSLATIONS[key] || key;
    }
    return DEFAULT_TRANSLATIONS[key] || key;
  }

  /**
   * Store all price data from a variant picker
   */
  function storePriceData(variantPicker) {
    const priceMap = new Map();
    const inputs = variantPicker.querySelectorAll('input[data-variant-id]');
    
    inputs.forEach(input => {
      const variantId = input.dataset.variantId;
      const priceFormatted = input.dataset.variantPriceFormatted;
      const price = input.dataset.variantPrice;
      const comparePriceFormatted = input.dataset.variantComparePriceFormatted;
      const comparePrice = input.dataset.variantComparePrice;
      
      if (variantId && priceFormatted) {
        priceMap.set(variantId, {
          price: price,
          priceFormatted: priceFormatted,
          comparePrice: comparePrice || null,
          comparePriceFormatted: comparePriceFormatted || null
        });
      }
    });
    
    return priceMap;
  }

  /**
   * Restore price elements and circle elements after morph
   */
  function restorePrices(variantPicker, priceMap) {
    const labels = variantPicker.querySelectorAll('.variant-option__button-label:not(.variant-option__button-label--has-swatch)');
    
    labels.forEach(label => {
      const input = label.querySelector('input[data-variant-id]');
      if (!input) return;
      
      const variantId = input.dataset.variantId;
      const textElement = label.querySelector('.variant-option__button-label__text');
      
      // Restore circle elements if missing
      let circleElement = label.querySelector('.co-variant-picker__circle');
      if (!circleElement && textElement) {
        circleElement = document.createElement('span');
        circleElement.className = 'co-variant-picker__circle z-div-absolute-height';
        circleElement.innerHTML = '<span class="co-variant-picker__circle-child z-div-absolute"></span>';
        textElement.insertAdjacentElement('beforebegin', circleElement);
      }
      
      // Skip price restoration if no price data
      if (!priceMap || priceMap.size === 0) return;
      
      const priceData = priceMap.get(variantId);
      if (!priceData) return;
      
      // Restore data attributes on input
      input.dataset.variantPrice = priceData.price;
      input.dataset.variantPriceFormatted = priceData.priceFormatted;
      if (priceData.comparePrice) {
        input.dataset.variantComparePrice = priceData.comparePrice;
        input.dataset.variantComparePriceFormatted = priceData.comparePriceFormatted;
      }
      
      // Check if price wrapper exists, if not create it
      let priceWrapper = label.querySelector('[data-variant-price-wrapper]');
      
      if (!priceWrapper && textElement) {
        priceWrapper = document.createElement('span');
        priceWrapper.className = 'variant-option__button-label__price-wrapper';
        priceWrapper.setAttribute('data-variant-price-wrapper', '');
        textElement.insertAdjacentElement('afterend', priceWrapper);
      }
      
      if (priceWrapper) {
        // Determine if this variant has a compare price (is on sale)
        const hasComparePrice = priceData.comparePrice && 
          parseInt(priceData.comparePrice) > parseInt(priceData.price);
        
        // Build the price HTML
        let priceHTML = '';
        
        if (hasComparePrice) {
          priceHTML += `<span class="variant-option__button-label__compare-price" data-variant-compare-price>${priceData.comparePriceFormatted}</span>`;
        }
        
        const saleClass = hasComparePrice ? ' variant-option__button-label__price--sale' : '';
        priceHTML += `<span class="variant-option__button-label__price${saleClass}" data-variant-price>${priceData.priceFormatted}</span>`;
        
        priceWrapper.innerHTML = priceHTML;
      }
    });
  }

  /**
   * Initialize variant sync for a container
   */
  function initVariantSync(container) {
    const variantPicker = container.querySelector(SELECTORS.variantPicker);
    const productForm = container.querySelector(SELECTORS.productForm);

    if (!variantPicker || !productForm) {
      return;
    }

    // Get product data from the variant picker's JSON script
    const productDataScript = variantPicker.querySelector('script[type="application/json"]');
    if (!productDataScript) {
      return;
    }

    // Store all variant data by building it from the option inputs
    const variantMap = buildVariantMap(variantPicker);
    
    // Store price data for restoration after morphs
    const priceMap = storePriceData(variantPicker);
    priceDataStore.set(container, priceMap);

    // Listen for changes on the variant picker
    variantPicker.addEventListener('change', (event) => {
      handleVariantChange(event, container, variantMap);
    });
    
    // Set up a MutationObserver to restore prices after Horizon morphs the DOM
    let isRestoring = false; // Flag to prevent infinite loops
    
    const observer = new MutationObserver((mutations) => {
      // Skip if we're currently restoring prices (prevents infinite loop)
      if (isRestoring) return;
      
      // Check if any mutations affected our price elements
      let needsRestore = false;
      
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Check if the mutation is from Horizon's morph (not our own restoration)
          // Look for significant structural changes, not just text content updates
          const removedNodes = Array.from(mutation.removedNodes);
          const addedNodes = Array.from(mutation.addedNodes);
          
          // Only restore if elements were actually added/removed (not just modified)
          const hasSignificantChanges = removedNodes.some(node => node.nodeType === 1) || 
                                        addedNodes.some(node => node.nodeType === 1);
          
          if (hasSignificantChanges) {
            needsRestore = true;
            break;
          }
        }
      }
      
      if (needsRestore) {
        const storedPrices = priceDataStore.get(container);
        if (storedPrices) {
          // Set flag to prevent observer from triggering during restoration
          isRestoring = true;
          
          // Use requestAnimationFrame to ensure DOM is stable
          requestAnimationFrame(() => {
            restorePrices(variantPicker, storedPrices);
            // Reset flag after a short delay to allow DOM to settle
            setTimeout(() => {
              isRestoring = false;
            }, 50);
          });
        }
      }
    });
    
    // Observe the variant picker for DOM changes
    observer.observe(variantPicker, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Build a map of option value IDs to variant data
   */
  function buildVariantMap(variantPicker) {
    const map = new Map();
    const inputs = variantPicker.querySelectorAll('input[data-variant-id], option[data-variant-id]');
    
    inputs.forEach(input => {
      const variantId = input.dataset.variantId;
      const optionValueId = input.dataset.optionValueId;
      const available = input.dataset.optionAvailable === 'true';
      
      if (variantId && optionValueId) {
        map.set(optionValueId, {
          id: variantId,
          available: available,
          optionValueId: optionValueId
        });
      }
    });

    return map;
  }

  /**
   * Get the currently selected variant from all selected options
   */
  function getSelectedVariant(variantPicker, variantMap) {
    // For single-option products, get the checked input's variant directly
    const checkedInputs = variantPicker.querySelectorAll('input:checked, select option:checked');
    
    if (checkedInputs.length === 1) {
      const input = checkedInputs[0];
      const variantId = input.dataset.variantId;
      const optionValueId = input.dataset.optionValueId;
      const available = input.dataset.optionAvailable === 'true';
      
      if (variantId) {
        return {
          id: variantId,
          available: available,
          optionValueId: optionValueId
        };
      }
    }

    // For multi-option products, we need to find the variant that matches all selected options
    // This requires the full variants array which we don't have easily accessible
    // So we'll use the last changed input's variant as a fallback
    const lastChecked = checkedInputs[checkedInputs.length - 1];
    if (lastChecked) {
      const variantId = lastChecked.dataset.variantId;
      const optionValueId = lastChecked.dataset.optionValueId;
      const available = lastChecked.dataset.optionAvailable === 'true';
      
      if (variantId) {
        return {
          id: variantId,
          available: available,
          optionValueId: optionValueId
        };
      }
    }

    return null;
  }

  /**
   * Handle variant change event
   */
  function handleVariantChange(event, container, variantMap) {
    const target = event.target;
    
    // Get variant info from the changed input
    let variantId, available;
    
    if (target.tagName === 'SELECT') {
      const selectedOption = target.options[target.selectedIndex];
      variantId = selectedOption.dataset.variantId;
      available = selectedOption.dataset.optionAvailable !== 'false';
    } else if (target.tagName === 'INPUT') {
      variantId = target.dataset.variantId;
      available = target.dataset.optionAvailable === 'true';
    }

    if (!variantId) {
      return;
    }

    // Update the product form
    updateProductForm(container, variantId, available);
  }

  /**
   * Update the product form with new variant data
   */
  function updateProductForm(container, variantId, available) {
    const productForm = container.querySelector(SELECTORS.productForm);
    if (!productForm) return;

    // 1. Update hidden variant ID input
    const variantInput = productForm.querySelector(SELECTORS.variantInput);
    if (variantInput) {
      variantInput.value = variantId;
    }

    // 2. Update add to cart button
    const addToCartButton = productForm.querySelector(SELECTORS.addToCartButton);
    const addToCartContainer = productForm.querySelector(SELECTORS.addToCartContainer);
    
    if (addToCartButton) {
      updateAddToCartButton(addToCartButton, available);
    }

    // 3. Update accelerated checkout visibility
    const acceleratedCheckout = productForm.querySelector(SELECTORS.acceleratedCheckout);
    if (acceleratedCheckout) {
      if (available) {
        acceleratedCheckout.removeAttribute('hidden');
      } else {
        acceleratedCheckout.setAttribute('hidden', 'true');
      }
    }

    // 4. Update quantity selector variant ID
    const quantitySelector = productForm.querySelector(SELECTORS.quantitySelector);
    if (quantitySelector) {
      quantitySelector.dataset.variantId = variantId;
    }


  }

  /**
   * Update the add to cart button state and text
   */
  function updateAddToCartButton(button, available) {
    // Update disabled state
    button.disabled = !available;

    // Update button text
    const textContent = button.querySelector('.add-to-cart-text__content');
    if (textContent) {
      if (available) {
        textContent.textContent = getTranslation('addToCart');
      } else {
        textContent.textContent = getTranslation('soldOut');
      }
    }

    // Update button classes if needed
    if (available) {
      button.classList.remove('button--disabled');
    } else {
      button.classList.add('button--disabled');
    }
  }

  /**
   * Initialize all variant sync containers on the page
   */
  function initAll() {
    const containers = document.querySelectorAll(SELECTORS.syncContainer);
    containers.forEach(initVariantSync);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Also initialize on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector(SELECTORS.syncContainer);
    if (container) {
      initVariantSync(container);
    }
  });

  // Expose for manual initialization if needed
  window.COVariantSync = {
    init: initVariantSync,
    initAll: initAll
  };

})();
